import { FormSectionHandler } from '../entity.handler';
import { ZoneResponse, ZoneUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, EnumToFormBuilderComboValues, NumberField, CostCentreField, SearchSelectField } from '../utils';
import { ICONS } from '../icons';
import { AssetGroupingService, MeasurementUnitsService } from 'app/services';

interface EditorValues {
    name?: string;
    group?: IdName;
    costCentre?: IdName;
    zoneType?: 'location' | 'keepin' | 'nogo' | 'route';
    speed?: number;
    radius?: number;
}

export class ZoneDetailsSectionHandler implements FormSectionHandler<ZoneResponse, EditorValues> {

    type = 'form';
    icon = ICONS.zone;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        private grouping: AssetGroupingService,
        private units: MeasurementUnitsService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['name', 'group', 'costCentre', 'zoneType', 'speed', 'radius'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ZONE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: ZoneResponse, payload: ZoneUpdateRequest): Promise<ZoneResponse> {
        return this.admin.applyUpdate(api => api.entities.updateZone(entity.id, payload), { _type: 'zone' });
    }

    mapEntityToEditor(entity: ZoneResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields(this.properties, entity, {}) as EditorValues;
        result.speed = result.speed ? this.units.fromBackend('speed', result.speed, 0) : null;
        result.radius = result.radius ? this.units.fromBackend('distance', result.radius, 3) : null;
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: ZoneUpdateRequest, values: EditorValues): Promise<ZoneUpdateRequest> {
        const result = EntityUtils.mergeFields(this.properties, values, entity) as ZoneUpdateRequest;

        if ('speed' in values) {
            result.speed = values.speed != null ? this.units.toBackend('speed', values.speed, 0) as number : null; // the backend requires an int
        }
        
        if ('radius' in values) {
            result.radius = values.radius != null ? this.units.toBackend('distance', values.radius) as number : null;
        }
        return Promise.resolve(result);
    }

    async getFields(entity: ZoneResponse): Promise<FormBuilderField[]> {
        const zoneTypes = ['location', 'keepin', 'nogo'];
        if (!this.creating) {
            zoneTypes.push('route'); // routes can't be created directly from admin (not yet anyway)
        }
        const fields = [
            TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
            // TODO: add tags once tag editor is fixed
            ComboField('zoneType', this.translate('FIELDS.TYPE'), true, EnumToFormBuilderComboValues(this.admin.i18n, 'SHARED.ZONE_TYPES', zoneTypes)),
            CostCentreField(this.ownerId, 'costCentre', this.translate('FIELDS.COST_CENTRE'), true),
            SearchSelectField('group', this.translate('FIELDS.GROUP'), true, {
                searchListPromise: async (_limit, filter) => ({ items: filter ? this.grouping.getZoneGroups(this.ownerId) : this.grouping.getZoneGroupsAsTree(this.ownerId) } as any),
                searchItemPromise: async id => this.admin.getCached(`getZoneGroups/${id}`, api => api.entities.getZoneGroup(id)),
            }),
            NumberField('speed', this.translate('FIELDS.SPEED'), false, 0, 1000, this.units.format(0, 'speed').unit),
            NumberField('radius', this.translate('FIELDS.RADIUS'), false, 0, 1000, this.units.format(0, 'distance').unit),

        ];
        return EntityUtils.prepareBulk(!entity, ['group', 'costCentre', 'zoneType', 'speed', 'radius'], fields);
    }

}
